import * as React from "react";
import { useContext, useState } from "react";
import getConfig from "next/config";
import Link from "next/link";
import { Button, Icon } from "@blueprintjs/core";
import { ApiContext, StoresContext } from "../../pages/_app";
import { observer } from "mobx-react";
import { useSearchParams } from "next/navigation";
import { RESTRICTED_COUNTRY_ARG_NAME } from "../../utils/constants";

const { publicRuntimeConfig } = getConfig();

type bannerTheme = "standard" | "warning";

const banners = [
  {
    name: "supporting-memberships",
    content: (
      <span>
        NEW: View our new&nbsp;
        <Link className="banner-link bp4-breadcrumb" href="/genomes?display=tree">
          taxonomic tree
        </Link>{" "}
        view of all microbial products!
        <br />
        Purchased a product from ATCC? Enter in the lot number to download the omics data or learn
        more about our supporting memberships&nbsp;
        <a href="https://www.atcc.org/applications/reference-quality-data/discover-the-atcc-genome-portal">
          here
        </a>
        .
      </span>
    ),
    hideWithActiveSubscription: true,
  },
  {
    name: "dev-env",
    content: <span>This is a DEV environment of the Genome Portal</span>,
    hideWithActiveSubscription: false,
    theme: "warning" as bannerTheme,
    irremovable: true,
  },
];

const enabledBannersInConfig = publicRuntimeConfig.ENABLED_BANNERS.split(",");
const enabledBanners = banners.filter((banner) => enabledBannersInConfig.includes(banner.name));

interface PageBannerProps {
  content: React.ReactNode;
  dismiss: () => void;
  theme?: bannerTheme;
  irremovable?: boolean;
}

const PageBanner = (props: PageBannerProps) => {
  return (
    <div
      className={`page-banners__banner ${
        props.theme === "warning" ? "page-banners__banner__warning" : ""
      }`}
    >
      {props.content}
      {props.irremovable || (
        <Button
          icon={<Icon icon="cross" size={12} />}
          small={true}
          minimal={true}
          onClick={props.dismiss}
        />
      )}
    </div>
  );
};

const PageBanners = () => {
  const rootStore = useContext(StoresContext);
  const dismissedBanners = rootStore.userStore.dismissedBanners;

  const displayedBanners = enabledBanners.filter((banner) => {
    if (dismissedBanners.includes(banner.name)) {
      return false;
    }
    return !(banner.hideWithActiveSubscription && rootStore.userStore.hasActiveSubscription());
  });
  if (displayedBanners.length === 0) {
    return null;
  }

  return (
    <div className="page-banners">
      {displayedBanners.map((banner) => (
        <PageBanner
          key={banner.name}
          content={banner.content}
          dismiss={() => rootStore.userStore.dismissBanner(banner.name)}
          theme={banner.theme}
          irremovable={banner.irremovable}
        />
      ))}
    </div>
  );
};

export const RestrictedCountryBanners = () => {
  const params = useSearchParams();
  const [restrictedCountryDismissed, setRestrictedCountryDismissed] = useState<boolean>(false);
  const isRestrictedCountryOn =
    !!params.get(RESTRICTED_COUNTRY_ARG_NAME) && !restrictedCountryDismissed;

  const api = useContext(ApiContext);
  const simulatedCountry = api.getSimulatedCountry();
  const isSimulatedCountryOn = !!simulatedCountry;

  if (!isRestrictedCountryOn && !isSimulatedCountryOn) {
    return null;
  }

  const resetSimulatedCountryLink = "/simulate-country?country=off";

  return (
    <div className="page-banners">
      {isRestrictedCountryOn ? (
        <PageBanner
          content={
            <span>
              Based on your location, access to certain features, data, or services may be
              restricted in accordance with applicable laws, regulations, and our compliance
              policies.
            </span>
          }
          dismiss={() => setRestrictedCountryDismissed(true)}
        />
      ) : null}
      {isSimulatedCountryOn ? (
        <PageBanner
          irremovable={true}
          theme={"warning"}
          dismiss={() => {
            /* empty */
          }}
          content={
            <span>
              <strong>DEBUG</strong>: Your country is set to <strong>{simulatedCountry}</strong>. Go
              to <a href={resetSimulatedCountryLink}>{resetSimulatedCountryLink}</a> to reset.
            </span>
          }
        />
      ) : null}
    </div>
  );
};

export default observer(PageBanners);
